import { Component, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { PROTOCOL_FORM_PARAMS } from 'src/app/models/protocol/protocol.model';
import { disableFields, enableFields, setProtocolFormValues } from '../../utils/protocolFormUtils';

const DEFAULT_FCC_EVENT_CLIENT_IP: string = 'AUTO';
const DEFAULT_FCC_EVENT_CLIENT_PORT: number = 2001;

@Component({
  selector: 'protocol-form',
  templateUrl: './protocol-form.component.html',
  styleUrls: ['./protocol-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProtocolFormComponent implements OnChanges {
  @Input() entity: any;
  @Input() set parentFormGroup(formGroup: FormGroup) {
    this._parentFormGroup = formGroup;
    this.createProtocolFormGroup();
  }
  @Input() set disableAll(disableProtocol: boolean) {
    this.protocolDisabled = disableProtocol;
  };
  @Input() enableFccEventFields: boolean = false;

  _parentFormGroup: FormGroup; 
  isToggleChecked: boolean;

  protocolDisabled: boolean;

  isFocused = false;

  constructor(
    private fb: FormBuilder
  ) { }

  ngOnChanges() {
    if (this.protocolDisabled) {
      this.disableProtocolFields();
    } else {
      this.enableProtocolFields();
    }
  }

  createProtocolFormGroup() {
    const protocolFormGroup = this._parentFormGroup.get('protocol') as FormGroup;

    protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode, this.fb.control(false));
    protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.ipAddress, this.fb.control(null, Validators.required));
    protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.port, this.fb.control(null, [Validators.required, Validators.min(1), Validators.max(65535), Validators.pattern('^[0-9]+$')]));
    protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.address, this.fb.control({ value: null, disabled: true }))
    protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.baudRate, this.fb.control({ value: null, disabled: true }));
    
    if (this.enableFccEventFields) {
      protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.forecourtEventClientIp, this.fb.control(DEFAULT_FCC_EVENT_CLIENT_IP));
      protocolFormGroup.addControl(PROTOCOL_FORM_PARAMS.forecourtEventClientPort, this.fb.control(DEFAULT_FCC_EVENT_CLIENT_PORT, [Validators.required, Validators.min(1), Validators.max(65535), Validators.pattern('^[0-9]+$')]));
    }

    this.onProtocolTypeChanges(protocolFormGroup);
    this.onForecourtEventClientIpChanges(protocolFormGroup);
    this.onForecourtEventClientPortChanges(protocolFormGroup);
    setProtocolFormValues(this._parentFormGroup, this.entity);
  }

  onProtocolTypeChanges(protocolForm: FormGroup) {
    protocolForm.get(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode).valueChanges.subscribe((type) => {
      if (type) {
        this.isToggleChecked = true;
        disableFields(protocolForm, [PROTOCOL_FORM_PARAMS.ipAddress, PROTOCOL_FORM_PARAMS.port]);
        enableFields(protocolForm, [PROTOCOL_FORM_PARAMS.address, PROTOCOL_FORM_PARAMS.baudRate]);
      } else {
        this.isToggleChecked = false;
        disableFields(protocolForm, [PROTOCOL_FORM_PARAMS.address, PROTOCOL_FORM_PARAMS.baudRate]);
        enableFields(protocolForm, [PROTOCOL_FORM_PARAMS.ipAddress, PROTOCOL_FORM_PARAMS.port]);
      }
    });
  }

  onForecourtEventClientIpChanges(protocolForm: FormGroup) {
    const forecourtEventClientIpControl = protocolForm.get(PROTOCOL_FORM_PARAMS.forecourtEventClientIp);
    if (forecourtEventClientIpControl) {
      forecourtEventClientIpControl.valueChanges
        .pipe(debounceTime(1000))
        .subscribe((ip) => {
          if (ip === null || ip === "") {
            forecourtEventClientIpControl.setValue(DEFAULT_FCC_EVENT_CLIENT_IP, { emitEvent: false });
          }
        });
    }
  }
  
  onForecourtEventClientPortChanges(protocolForm: FormGroup) {
    const forecourtEventClientPortControl = protocolForm.get(PROTOCOL_FORM_PARAMS.forecourtEventClientPort);
    if (forecourtEventClientPortControl) {
      forecourtEventClientPortControl.valueChanges
        .pipe(debounceTime(1000))
        .subscribe((port) => {
          if (port === null || port === "") {
            forecourtEventClientPortControl.setValue(DEFAULT_FCC_EVENT_CLIENT_PORT, { emitEvent: false });
          }
        });
    }
  }

  disableProtocolFields() {
    const protocolFormGroup = this._parentFormGroup.get('protocol') as FormGroup;
    
    disableFields(
      protocolFormGroup, 
      [
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode,
        PROTOCOL_FORM_PARAMS.ipAddress,
        PROTOCOL_FORM_PARAMS.port,
        PROTOCOL_FORM_PARAMS.address,
        PROTOCOL_FORM_PARAMS.baudRate,
      ],
    );
  }

  enableProtocolFields() {
    const protocolFormGroup = this._parentFormGroup.get('protocol') as FormGroup;
    this.createProtocolFormGroup();
    enableFields(protocolFormGroup, [PROTOCOL_FORM_PARAMS.communicationMethodTypeCode]);
  }
}
